import { Ref, forwardRef } from 'react'

import { Box, useClassNames } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { observer } from 'mobx-react'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString } from 'modules/core/lib/dates'
import { InjectHoverProvider } from 'modules/core/lib/hoverContext'

import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import type View from 'modules/shift_management/store/internal/models/View'

import type { Column } from '../utils/buildColumns'
import HeaderContent from './HeaderContent'
import * as stylesRefs from './index.vanilla.css'

interface CellProps {
  heading: Column
  index: number
  view: View
  canManageShifts: boolean
  onSelect: (index: number, moment: moment.Moment) => void
  checked: (index: number, dayId: string) => boolean
}

const HeaderCell = (
  { heading, index, onSelect, checked, view, canManageShifts }: CellProps,
  ref: Ref<HTMLDivElement>
) => {
  const { viewType, groupType, lastDay } = view
  const { date } = heading
  const cx = useClassNames(stylesRefs)
  const currentDate = moment().startOf('hour') // Use 'hour' to work with daily view too
  const isToday = dateToString(date, view.dateFormat) === dateToString(currentDate, view.dateFormat)
  // To not highlight first column (employee or work area) that always has the date of today
  const highlighted = isToday && index !== 0
  const dayId = dateToString(date, view.dateFormat)
  const isChecked = checked(index, dayId)
  const sticky = index === 0
  const isEndOfWeek = date.clone().endOf('day').isSame(date.clone().endOf('week'))
  const isEndOfPeriod = lastDay.isSame(date?.clone().endOf('day'))
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const newEntryFlowEnabled = hasFeatureFlag('dev_shift_management_autoscheduler_entry_flow')
  const onClick = () => {
    track(trackingEvents.shiftManagement.bulkDaySelected, {
      selected: !isChecked,
      view: viewType,
      view_type_by: groupType,
    })
    return onSelect(index, date)
  }
  return (
    <InjectHoverProvider key={index}>
      <Box
        ref={ref}
        background={newEntryFlowEnabled ? 'white' : 'grey100'}
        height={newEntryFlowEnabled ? 's32' : 's24'}
        paddingLeft={newEntryFlowEnabled ? (index == 0 ? 's1' : 's8') : index == 0 ? 's6' : 's4'}
        paddingRight={newEntryFlowEnabled ? (index == 0 ? 's4' : 's8') : index == 0 ? 's6' : 's4'}
        border={{
          right: {
            width: 's1',
            style: 'solid',
            color: newEntryFlowEnabled
              ? 'grey200'
              : isEndOfWeek && !isEndOfPeriod
              ? 'grey400'
              : 'grey300',
          },
          bottom: { width: 's1', style: 'solid', color: 'grey200' },
        }}
        className={cx('header', { sticky })}
      >
        <Box flexDirection="row" alignItems="center" gap="s6" height="full">
          <HeaderContent
            highlighted={highlighted}
            heading={heading}
            index={index}
            onClick={onClick}
            isChecked={isChecked}
            canManageShifts={canManageShifts}
          />
        </Box>
      </Box>
    </InjectHoverProvider>
  )
}

export default observer(forwardRef(HeaderCell))
